import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputAdornment from "@mui/material/InputAdornment";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PageGuide from "components/admin/PageGuide";
import Footer from "examples/Footer";
import PageCard from "components/admin/PageCard";
import AdminTable from "components/admin/AdminTable";
import { useConfirm } from "components/admin/Feedback";
import { useFetch, useRunner } from "components/admin/hooks";
import api from "api/client";
import { useCan } from "auth/AuthContext";
import { PERMISSIONS } from "auth/access";
import { formatDate } from "utils/format";

/*
 * Bố cục (người dùng: CSKH / vận hành giữ chat sạch):
 *   [Thử một câu chat] — xem người chơi sẽ thấy gì.
 *   [Từ cấm]: thêm từ (gợi ý thêm cả bản không dấu) · tìm · danh sách chip có nút bỏ ·
 *             thanh "N thay đổi chưa lưu" + Lưu / Bỏ thay đổi (hỏi lại, ghi rõ thêm / bỏ từ nào) ·
 *             Nâng cao (gập): sửa cả danh sách dạng chữ, thêm bộ từ mặc định.
 *   [Đang bị khoá chat]: còn bao lâu, lý do, ai khoá, gỡ khoá.
 */

const SHOW_LIMIT = 150;
const BIG_REMOVE = 20;

/** Bỏ dấu tiếng Việt: "địt" → "dit". */
const stripAccents = (text) =>
  text.normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/đ/g, "d").replace(/Đ/g, "D");

const normalizeWord = (w) => w.trim().replace(/\s+/g, " ").toLowerCase();

const splitInput = (text) =>
  text
    .split(/[\n,;]/)
    .map(normalizeWord)
    .filter(Boolean);

function remaining(until) {
  const ms = new Date(until).getTime() - Date.now();
  if (ms <= 0) return "sắp hết";
  const minutes = Math.ceil(ms / 60000);
  if (minutes < 60) return `${minutes} phút`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} giờ`;
  const days = Math.floor(hours / 24);
  return days > 3650 ? "vĩnh viễn" : `${days} ngày`;
}

function DiffList({ label, words, color }) {
  if (!words.length) return null;
  return (
    <MDBox mb={1}>
      <MDTypography variant="caption" fontWeight="bold" display="block" mb={0.5}>
        {label} ({words.length})
      </MDTypography>
      <MDBox display="flex" gap={0.5} flexWrap="wrap">
        {words.slice(0, 40).map((w) => (
          <Chip key={w} size="small" color={color} variant="outlined" label={w} />
        ))}
        {words.length > 40 && <Chip size="small" label={`… và ${words.length - 40} từ nữa`} />}
      </MDBox>
    </MDBox>
  );
}

DiffList.propTypes = {
  label: PropTypes.string.isRequired,
  words: PropTypes.arrayOf(PropTypes.string).isRequired,
  color: PropTypes.string.isRequired,
};

function ChatModeration() {
  const confirm = useConfirm();
  const [run, busy] = useRunner();
  const words = useFetch(() => api.get("/api/admin/chat/words"), []);
  const mutes = useFetch(() => api.get("/api/admin/chat/mutes"), []);
  const canEdit = useCan(PERMISSIONS.CHAT_EDIT);
  const canUnmute = useCan(PERMISSIONS.PLAYER_MODERATE) || canEdit;

  const saved = useMemo(() => words.data || [], [words.data]);
  const [draft, setDraft] = useState([]);
  const [newWord, setNewWord] = useState("");
  const [withPlain, setWithPlain] = useState(true);
  const [search, setSearch] = useState("");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [sample, setSample] = useState("");
  const [filtered, setFiltered] = useState(null);

  useEffect(() => {
    setDraft(saved);
    setBulkText(saved.join("\n"));
  }, [saved]);

  const savedSet = useMemo(() => new Set(saved), [saved]);
  const draftSet = useMemo(() => new Set(draft), [draft]);
  const added = draft.filter((w) => !savedSet.has(w));
  const removed = saved.filter((w) => !draftSet.has(w));
  const dirty = added.length + removed.length > 0;

  // Rời trang khi còn thay đổi chưa lưu thì trình duyệt hỏi lại.
  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const pending = splitInput(newWord);
  const plainExtras = withPlain
    ? pending.map(stripAccents).filter((w, i) => w !== pending[i] && !pending.includes(w))
    : [];
  const toAdd = [...new Set([...pending, ...plainExtras])].filter((w) => !draftSet.has(w));
  const alreadyIn = pending.filter((w) => draftSet.has(w));

  const addWords = () => {
    if (!toAdd.length) return;
    setDraft([...toAdd, ...draft]);
    setNewWord("");
  };

  const removeWord = (w) => setDraft(draft.filter((d) => d !== w));

  const shown = useMemo(() => {
    const q = normalizeWord(search);
    const list = q
      ? draft.filter((w) => w.includes(q) || stripAccents(w).includes(stripAccents(q)))
      : draft;
    return list;
  }, [draft, search]);

  const save = async () => {
    const bigRemove = removed.length >= BIG_REMOVE || (saved.length > 0 && draft.length === 0);
    const ok = await confirm({
      title: bigRemove ? "Bỏ NHIỀU từ cấm" : "Lưu danh sách từ cấm",
      message: [
        added.length
          ? `Thêm ${added.length} từ: ${added.slice(0, 15).join(", ")}${
              added.length > 15 ? "…" : ""
            }`
          : null,
        removed.length
          ? `Bỏ ${removed.length} từ: ${removed.slice(0, 15).join(", ")}${
              removed.length > 15 ? "…" : ""
            }`
          : null,
        `Sau khi lưu còn ${draft.length} từ, áp dụng ngay cho mọi kênh chat.`,
        bigRemove ? "\nBạn đang bỏ rất nhiều từ — chắc chắn không phải lỡ tay?" : null,
      ]
        .filter(Boolean)
        .join("\n"),
      color: bigRemove ? "error" : "info",
      confirmText: "Lưu",
    });
    if (!ok) return;
    const res = await run(
      () => api.post("/api/admin/chat/words", { text: draft.join("\n") }),
      (data) => `Đã lưu, danh sách có ${data.length} từ`
    );
    if (res.ok) words.setData(res.result);
  };

  const discard = () => {
    setDraft(saved);
    setBulkText(saved.join("\n"));
  };

  const applyBulk = () => setDraft([...new Set(splitInput(bulkText))]);

  const mergeDefaults = async () => {
    if (dirty) {
      await confirm({
        title: "Còn thay đổi chưa lưu",
        message: "Lưu hoặc bỏ các thay đổi đang có trước khi thêm bộ từ mặc định.",
        confirmText: "Đã hiểu",
      });
      return;
    }
    const ok = await confirm({
      title: "Thêm bộ từ cấm mặc định",
      message:
        "Gộp khoảng 600 từ tục / tình dục có sẵn vào danh sách hiện tại, áp dụng ngay. Từ bạn đã thêm vẫn được giữ.",
      confirmText: "Thêm bộ mặc định",
    });
    if (!ok) return;
    const res = await run(
      () => api.post("/api/admin/chat/words/defaults"),
      (data) => `Đã thêm ${data.added} từ`
    );
    if (res.ok) words.setData(res.result.words);
  };

  const testFilter = async () => {
    if (!sample.trim()) return;
    const res = await run(() => api.post("/api/admin/chat/words/test", { text: sample }));
    if (res.ok) setFiltered(res.result);
  };

  const unmute = async (m) => {
    const ok = await confirm({
      title: "Gỡ khoá chat",
      message: `Cho ${m.name} chat lại ngay? (đang bị khoá thêm ${remaining(m.until)})`,
      color: "success",
      confirmText: "Gỡ khoá chat",
    });
    if (!ok) return;
    const res = await run(
      () => api.post(`/api/admin/players/${m.userId}/unmute`),
      `Đã gỡ khoá chat cho ${m.name}`
    );
    if (res.ok) mutes.reload(true);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <PageGuide page="chat" />
        <Grid container spacing={3}>
          <Grid item xs={12} lg={6}>
            {/* Bọc để thẻ cao vừa nội dung, không kéo dài theo cột bên cạnh. */}
            <MDBox>
              <PageCard
                title="Thử một câu chat"
                subtitle="Gõ thử để xem người chơi sẽ thấy câu đó ra sao"
                color="dark"
              >
                <MDBox
                  component="form"
                  display="flex"
                  gap={1}
                  alignItems="center"
                  onSubmit={(e) => {
                    e.preventDefault();
                    testFilter();
                  }}
                >
                  <MDInput
                    fullWidth
                    label="Câu chat"
                    value={sample}
                    onChange={(e) => {
                      setSample(e.target.value);
                      setFiltered(null);
                    }}
                  />
                  <MDButton
                    type="submit"
                    variant="gradient"
                    color="info"
                    disabled={busy || !sample.trim()}
                  >
                    Thử
                  </MDButton>
                </MDBox>
                {filtered !== null && (
                  <Alert severity={filtered === sample ? "success" : "warning"} sx={{ mt: 2 }}>
                    <MDTypography variant="caption" display="block">
                      {filtered === sample
                        ? "Không có từ nào bị chặn. Người chơi thấy nguyên câu:"
                        : "Có từ bị chặn. Người chơi thấy:"}
                    </MDTypography>
                    <b>{filtered}</b>
                  </Alert>
                )}
                {dirty && (
                  <MDTypography variant="caption" color="text" display="block" mt={1}>
                    Lưu ý: thử theo danh sách ĐÃ LƯU, chưa tính các thay đổi chưa lưu.
                  </MDTypography>
                )}
              </PageCard>

              <PageCard
                title={`Từ cấm (${draft.length})`}
                subtitle="Bị thay bằng *** ở chat khu vực, thế giới, bang hội và tin nhắn riêng"
              >
                <MDBox display="flex" flexDirection="column" gap={2}>
                  {words.error && (
                    <Alert severity="error">Không tải được danh sách: {words.error}</Alert>
                  )}

                  {canEdit && (
                    <MDBox
                      component="form"
                      onSubmit={(e) => {
                        e.preventDefault();
                        addWords();
                      }}
                    >
                      <MDBox display="flex" gap={1} alignItems="flex-start">
                        <MDInput
                          fullWidth
                          label="Thêm từ cấm"
                          placeholder="Ví dụ: đồ khốn, ngu như bò"
                          value={newWord}
                          onChange={(e) => setNewWord(e.target.value)}
                        />
                        <MDButton
                          type="submit"
                          variant="gradient"
                          color="info"
                          disabled={!toAdd.length}
                          sx={{ flexShrink: 0 }}
                        >
                          <Icon>add</Icon>&nbsp;Thêm
                        </MDButton>
                      </MDBox>
                      <FormControlLabel
                        sx={{ ml: 0 }}
                        control={
                          <Checkbox
                            size="small"
                            checked={withPlain}
                            onChange={(e) => setWithPlain(e.target.checked)}
                          />
                        }
                        label={
                          <MDTypography variant="caption">
                            Thêm cả bản không dấu (bộ lọc phân biệt dấu: chặn &quot;địt&quot; không
                            chặn được &quot;dit&quot;). Nhiều từ thì cách nhau bằng dấu phẩy.
                          </MDTypography>
                        }
                      />
                      {(toAdd.length > 0 || alreadyIn.length > 0) && (
                        <MDTypography variant="caption" color="text" display="block">
                          {toAdd.length > 0 && `Sẽ thêm: ${toAdd.join(", ")}. `}
                          {alreadyIn.length > 0 && `Đã có sẵn: ${alreadyIn.join(", ")}.`}
                        </MDTypography>
                      )}
                    </MDBox>
                  )}

                  {dirty && (
                    <MDBox
                      p={1.5}
                      borderRadius="lg"
                      sx={{ border: 2, borderColor: "warning.main" }}
                    >
                      <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
                        {added.length + removed.length} thay đổi chưa lưu — người chơi chưa bị ảnh
                        hưởng cho tới khi bấm Lưu
                      </MDTypography>
                      <DiffList label="Thêm" words={added} color="success" />
                      <DiffList label="Bỏ" words={removed} color="error" />
                      <MDBox display="flex" gap={1} mt={1}>
                        <MDButton variant="gradient" color="info" disabled={busy} onClick={save}>
                          <Icon>save</Icon>&nbsp;Lưu thay đổi
                        </MDButton>
                        <MDButton
                          variant="text"
                          color="secondary"
                          disabled={busy}
                          onClick={discard}
                        >
                          Bỏ thay đổi
                        </MDButton>
                      </MDBox>
                    </MDBox>
                  )}

                  <MDInput
                    fullWidth
                    size="small"
                    label="Tìm trong danh sách"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Icon fontSize="small">search</Icon>
                        </InputAdornment>
                      ),
                    }}
                  />
                  {words.loading && !words.data ? (
                    <MDTypography variant="button" color="text">
                      Đang tải…
                    </MDTypography>
                  ) : (
                    <MDBox>
                      <MDBox
                        display="flex"
                        gap={0.5}
                        flexWrap="wrap"
                        maxHeight={320}
                        overflow="auto"
                        p={0.5}
                      >
                        {shown.slice(0, SHOW_LIMIT).map((w) => (
                          <Chip
                            key={w}
                            size="small"
                            label={w}
                            color={savedSet.has(w) ? "default" : "success"}
                            onDelete={canEdit ? () => removeWord(w) : undefined}
                          />
                        ))}
                        {shown.length === 0 && (
                          <MDTypography variant="caption" color="text">
                            {search ? `Không có từ nào chứa "${search}".` : "Danh sách đang trống."}
                          </MDTypography>
                        )}
                      </MDBox>
                      {shown.length > SHOW_LIMIT && (
                        <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                          Đang hiện {SHOW_LIMIT} / {shown.length} từ — gõ vào ô tìm để thấy từ khác.
                        </MDTypography>
                      )}
                    </MDBox>
                  )}
                  <MDTypography variant="caption" color="text">
                    Chỉ chặn trọn từ: cấm &quot;cc&quot; không làm sao chữ &quot;acc&quot;. Không
                    phân biệt hoa thường.
                  </MDTypography>

                  {canEdit && (
                    <MDBox>
                      <MDButton
                        variant="text"
                        color="secondary"
                        size="small"
                        onClick={() => setAdvancedOpen((v) => !v)}
                        sx={{ px: 0 }}
                      >
                        <Icon>{advancedOpen ? "expand_less" : "expand_more"}</Icon>&nbsp;Nâng cao
                      </MDButton>
                      <Collapse in={advancedOpen}>
                        <MDBox display="flex" flexDirection="column" gap={1.5} pt={1}>
                          <MDTypography variant="caption" color="text">
                            Sửa cả danh sách dạng chữ (mỗi dòng một từ), bấm Áp dụng rồi xem lại
                            phần thay đổi trước khi Lưu.
                          </MDTypography>
                          <MDInput
                            multiline
                            minRows={6}
                            maxRows={16}
                            value={bulkText}
                            onChange={(e) => setBulkText(e.target.value)}
                          />
                          <MDBox display="flex" gap={1} flexWrap="wrap">
                            <MDButton
                              size="small"
                              variant="outlined"
                              color="info"
                              onClick={applyBulk}
                            >
                              Áp dụng vào danh sách
                            </MDButton>
                            <MDButton
                              size="small"
                              variant="outlined"
                              color="info"
                              disabled={busy || words.loading}
                              onClick={mergeDefaults}
                            >
                              <Icon>playlist_add</Icon>&nbsp;Thêm bộ từ mặc định (~600 từ)
                            </MDButton>
                          </MDBox>
                        </MDBox>
                      </Collapse>
                    </MDBox>
                  )}
                </MDBox>
              </PageCard>
            </MDBox>
          </Grid>

          <Grid item xs={12} lg={6}>
            <MDBox>
              <PageCard
                title={`Đang bị khoá chat${mutes.data ? ` (${mutes.data.length})` : ""}`}
                subtitle="Muốn khoá chat ai: mở người đó ở trang Người chơi"
                color="warning"
                noPadding
              >
                {mutes.error && (
                  <MDBox px={2}>
                    <Alert severity="error">Không tải được: {mutes.error}</Alert>
                  </MDBox>
                )}
                <AdminTable
                  loading={mutes.loading}
                  rows={mutes.data || []}
                  rowKey="userId"
                  emptyText="Không ai đang bị khoá chat"
                  columns={[
                    {
                      key: "name",
                      label: "Người chơi",
                      render: (m) => (
                        <MDBox display="flex" gap={0.5} alignItems="center">
                          <Link to={`/players/${m.userId}`}>{m.name}</Link>
                          {m.online && <Chip size="small" color="success" label="đang chơi" />}
                        </MDBox>
                      ),
                    },
                    {
                      key: "until",
                      label: "Còn bị khoá",
                      render: (m) => (
                        <MDBox>
                          {remaining(m.until)}
                          <MDTypography variant="caption" color="text" display="block">
                            tới {formatDate(m.until)}
                          </MDTypography>
                        </MDBox>
                      ),
                    },
                    {
                      key: "reason",
                      label: "Lý do",
                      wrap: true,
                      render: (m) => m.reason || "Không ghi",
                    },
                    { key: "admin", label: "Người khoá", render: (m) => m.admin || "-" },
                    canUnmute && {
                      key: "actions",
                      label: "",
                      align: "right",
                      render: (m) => (
                        <MDButton
                          size="small"
                          variant="outlined"
                          color="success"
                          disabled={busy}
                          onClick={() => unmute(m)}
                        >
                          Gỡ khoá
                        </MDButton>
                      ),
                    },
                  ].filter(Boolean)}
                />
              </PageCard>
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default ChatModeration;
